import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { FC, useCallback } from "react";
import { Command } from "@vendor/cmdk/cmdk";
import {
    ActivityIcon,
    CalendarIcon,
    ChartOfAccountsIcon,
    CogwheelIcon,
    DollarCircleIcon,
    GrowthIcon,
    HomeIcon,
    ProfileIcon,
    ServicesIcon,
    TasksIcon,
    WorkIcon,
} from "@icons";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { useCanManageOrganizationTaskTemplates } from "@hooks/permissions/organization/useCanManageOrganizationTaskTemplates";
import { useCanViewOrganizationBilling } from "@hooks/permissions/organization/useCanViewOrganizationBilling";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useSettings } from "@components/settings/SettingsProvider";
import { OrganizationSettingsPages } from "@components/settings/constants";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { useOrgNavigationShortcutKeys } from "@components/navigation/useResolvedOrgNavigationShortcuts";
import { useCommands } from "./useCommands";

export const OrganizationNavigationCommands: FC = () => {
    const navigate = useStateAwareNavigate();
    const { close: closeCommands } = useCommands();
    const { open: openSettings } = useSettings();
    const canViewBilling = useCanViewOrganizationBilling();
    const canManageSettings = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_SETTINGS,
    );
    const canManageTaskTemplates = useCanManageOrganizationTaskTemplates();
    const { closeManagement, reportTemplates } = useTypedFlags();
    const shortcutKeys = useOrgNavigationShortcutKeys();

    const handleSelected = useCallback(
        (path: string) => {
            navigate(path);
            closeCommands();
        },
        [closeCommands, navigate],
    );

    return (
        <Command.Group heading="Navigation">
            <CommandItem
                shortcut={shortcutKeys("clients")}
                onSelect={() => handleSelected("/")}
            >
                <ActivityIcon /> Go to Clients
            </CommandItem>

            <CommandItem
                shortcut={shortcutKeys("team")}
                onSelect={() => handleSelected("/team")}
            >
                <ProfileIcon /> Go to Team
            </CommandItem>

            {canViewBilling && (
                <CommandItem
                    shortcut={shortcutKeys("billing")}
                    onSelect={() => handleSelected("/organization-billing")}
                >
                    <DollarCircleIcon /> Go to Billing
                </CommandItem>
            )}

            {canManageSettings && (
                <CommandItem
                    shortcut={shortcutKeys("settings")}
                    onSelect={() => {
                        openSettings([
                            OrganizationSettingsPages.FIRM_INFORMATION,
                        ]);
                        closeCommands();
                    }}
                >
                    <CogwheelIcon /> Go to Settings
                </CommandItem>
            )}

            {canManageSettings && (
                <CommandItem
                    onSelect={() => {
                        openSettings([OrganizationSettingsPages.TASK_GROUPS]);
                        closeCommands();
                    }}
                >
                    <TasksIcon /> Go to Task groups
                </CommandItem>
            )}

            {canManageSettings && closeManagement && (
                <CommandItem
                    shortcut={shortcutKeys("services")}
                    onSelect={() => handleSelected("/templates/services")}
                >
                    <ServicesIcon /> Go to Service templates
                </CommandItem>
            )}

            {closeManagement && (
                <CommandItem
                    shortcut={shortcutKeys("schedules")}
                    onSelect={() => handleSelected("/templates/schedules")}
                >
                    <CalendarIcon /> Go to Service schedules
                </CommandItem>
            )}

            {closeManagement && canManageTaskTemplates && (
                <CommandItem
                    onSelect={() => handleSelected("/templates/tasks")}
                >
                    <TasksIcon /> Go to Tasks
                </CommandItem>
            )}

            {canManageSettings && (
                <CommandItem
                    shortcut={shortcutKeys("chart-of-accounts-templates")}
                    keywords={["chart of accounts templates"]}
                    onSelect={() =>
                        handleSelected("/templates/chart-of-accounts")
                    }
                >
                    <ChartOfAccountsIcon /> Go to CoA templates
                </CommandItem>
            )}

            {reportTemplates && (
                <CommandItem
                    shortcut={shortcutKeys("report-templates")}
                    onSelect={() => handleSelected("/templates/reports")}
                >
                    <GrowthIcon /> Go to Report templates
                </CommandItem>
            )}

            {closeManagement && (
                <CommandItem
                    shortcut={shortcutKeys("tasks")}
                    onSelect={() => handleSelected("/clients-tasks")}
                >
                    <WorkIcon /> Go to Actions
                </CommandItem>
            )}

            {closeManagement && (
                <CommandItem
                    shortcut={shortcutKeys("work")}
                    onSelect={() => handleSelected("/clients-work")}
                >
                    <HomeIcon /> Go to Home
                </CommandItem>
            )}
        </Command.Group>
    );
};
